import {createFeatureSelector, createSelector} from '@ngrx/store';
import {CartItem} from '../../data/model';
import {selectProgramEventCost} from '../context-data/context-data.selectors';
import {buildBundleInstanceId} from './bundle-instance-id';
import {buildCartItemSpecLabel} from './cart-item-spec-label';
import * as fromCart from './cart.reducer';

export const selectCartState = createFeatureSelector<fromCart.CartState>(
  fromCart.cartFeatureKey,
);

/**
 * Selects all items within the current cart.
 */
export const selectCartItems = createSelector(
  selectCartState,
  fromCart.getCartItems,
);

/**
 * Selects cart items excluding promotional items.
 */
export const selectCartItemsSansPromotional = createSelector(
  selectCartItems,
  items => {
    return items.filter(item => !item.isPromotionalItem);
  },
);

/**
 * Selects cart items excluding shipping-related items for cart display.
 * Shipping items should only be visible at checkout.
 */
export const selectCartItemsForDisplay = createSelector(
  selectCartItemsSansPromotional,
  (cartItems: CartItem[]) => {
    const visibleItems = cartItems.filter(item => {
      // Filter out shipping-related items based on templateKey or fulfilledBy
      // Check if item has shipping-related template keys
      const shippingTemplateKeys = ['shipping', 'shipping-fee'];
      const hasShippingTemplateKey = shippingTemplateKeys.includes(
        item.templateKey,
      );

      // Check if item is fulfilled by shipping
      const isShippingFulfilled = item.fulfilledBy === 'shipping';

      // Check if item name contains shipping-related terms
      const hasShippingInName =
        item.name?.toLowerCase().includes('shipping') ||
        item.name?.toLowerCase().includes('shipping fee');

      // Combine all shipping-related checks
      const isShippingItem =
        hasShippingTemplateKey || isShippingFulfilled || hasShippingInName;
      return !isShippingItem;
    });

    return groupBundleCartItems(visibleItems);
  },
);

/**
 * Returns true if the cart should be shown, false otherwise.
 */
export const selectShowCart = createSelector(
  selectCartState,
  fromCart.getShowCart,
);

/**
 * Returns true if the cart should be shown, false otherwise.
 */
export const selectEnableCart = createSelector(
  selectCartState,
  fromCart.getEnableCart,
);

/**
 * Returns true if the cart is in loading state.
 */
export const selectCartLoadingState = createSelector(
  selectCartState,
  fromCart.getLoadingStatus,
);

/**
 * Returns the sum of cart items excluding shipping for display purposes.
 * This ensures shipping fees are not included in the cart subtotal display.
 */
export const selectCartSubtotalForDisplay = createSelector(
  selectCartItemsForDisplay,
  selectProgramEventCost,
  (cartItems: CartItem[], programCost: number | null | undefined) => {
    const totalCents = cartItems.reduce(
      (previousValue: number, current: CartItem) =>
        previousValue + 100 * current.price(),
      0,
    );
    const requiredItemCost = programCost ? programCost * 100 : 0;
    return (totalCents + requiredItemCost) / 100;
  },
);

/**
 * Returns true if the cart is empty and not in a loading state.
 */

export const selectCheckEmptyCart = createSelector(
  selectCartItems,
  selectCartLoadingState,
  (cartItems: CartItem[], loadingState: boolean) => {
    const emptyCart = cartItems.length === 0 && !loadingState;
    return emptyCart;
  },
);

export const selectFailedAddons = createSelector(
  selectCartState,
  fromCart.getFailedItems,
);

export const selectTax = createSelector(selectCartState, fromCart.getTax);

/**
 * For items that have a `bundleId` but no `bundle_instance_id` (e.g. when the
 * API does not round-trip the xp field), reconstruct synthetic instance ids so
 * that distinct bundle purchases are not collapsed into one cart row.
 *
 * Within a `bundleId` group each `productId` appears at most once per bundle
 * instance.  We use this property to distribute items into separate instances.
 */
function assignMissingBundleInstanceIds(cartItems: CartItem[]): CartItem[] {
  const indexesByBundle = collectBundleIndexesMissingInstanceId(cartItems);

  if (indexesByBundle.size === 0) return cartItems;

  // Shallow-copy so we never mutate the store's array or its CartItem objects.
  const result = [...cartItems];

  for (const indices of indexesByBundle.values()) {
    const instances = distributeIntoInstances(indices, result);
    stampSyntheticIds(instances, result);
  }

  return result;
}

/** Collects array indices of items that need a synthetic id, grouped by bundleId. */
function collectBundleIndexesMissingInstanceId(
  cartItems: CartItem[],
): Map<number, number[]> {
  const map = new Map<number, number[]>();
  for (let idx = 0; idx < cartItems.length; idx++) {
    const bundleKey = bundleIdNeedingSyntheticInstanceId(cartItems[idx]);
    if (bundleKey === undefined) continue;

    let list = map.get(bundleKey);
    if (!list) {
      list = [];
      map.set(bundleKey, list);
    }
    list.push(idx);
  }
  return map;
}

/** Returns the numeric bundleId when the item needs a synthetic instance id, or undefined. */
function bundleIdNeedingSyntheticInstanceId(
  item: CartItem,
): number | undefined {
  if (
    item.bundleId === undefined ||
    item.bundleId === null ||
    !Number.isFinite(Number(item.bundleId))
  ) {
    return undefined;
  }
  if (item.bundle_instance_id && item.bundle_instance_id.length > 0) {
    return undefined;
  }
  return Number(item.bundleId);
}

/**
 * Distributes item indices into instance buckets so that each `productId`
 * appears at most once per instance.
 */
function distributeIntoInstances(
  indices: number[],
  items: CartItem[],
): number[][] {
  const instances: number[][] = [];
  for (const idx of indices) {
    const target = instances.find(
      inst => !inst.some(i => items[i].productId === items[idx].productId),
    );
    if (target) {
      target.push(idx);
    } else {
      instances.push([idx]);
    }
  }
  return instances;
}

/** Computes a synthetic `bundle_instance_id` for each instance and writes copies into `result`. */
function stampSyntheticIds(instances: number[][], result: CartItem[]): void {
  for (const instance of instances) {
    const syntheticId = buildBundleInstanceId(
      result[instance[0]].bundleId!,
      instance.map(idx => result[idx]),
    );
    for (const idx of instance) {
      const copy = new CartItem(result[idx]);
      copy.bundle_instance_id = syntheticId;
      result[idx] = copy;
    }
  }
}

function groupBundleCartItems(cartItems: CartItem[]): CartItem[] {
  cartItems = assignMissingBundleInstanceIds(cartItems);
  const result: CartItem[] = [];
  const bundleGroups = new Map<string, CartItem>();

  for (const item of cartItems) {
    const bundleGroupKey = getBundleGroupKey(item);
    if (!bundleGroupKey) {
      result.push(item);
      continue;
    }

    const existingGroup = bundleGroups.get(bundleGroupKey);
    if (!existingGroup) {
      const groupedItems = [new CartItem(item)];
      const quantity = item.quantity || 1;
      const totalPrice = groupedItems.reduce(
        (sum, groupedItem) => sum + groupedItem.price(),
        0,
      );

      const group = new CartItem({
        ...item,
        id: `bundle:${bundleGroupKey}`,
        productId: `bundle:${item.bundleId ?? bundleGroupKey}`,
        name: bundleDisplayName(item),
        quantity,
        unitPrice: quantity > 0 ? totalPrice / quantity : 0,
        specs: [],
        bundleGroupItems: groupedItems,
        bundleGroupLines: groupedItems.map(buildBundleLine),
      });

      bundleGroups.set(bundleGroupKey, group);
      result.push(group);
      continue;
    }

    const groupedItems = [
      ...(existingGroup.bundleGroupItems ?? []),
      new CartItem(item),
    ];
    const totalPrice = groupedItems.reduce(
      (sum, groupedItem) => sum + groupedItem.price(),
      0,
    );
    const quantity = existingGroup.quantity || 1;

    existingGroup.bundleGroupItems = groupedItems;
    existingGroup.bundleGroupLines = groupedItems.map(buildBundleLine);
    existingGroup.unitPrice = quantity > 0 ? totalPrice / quantity : 0;
  }

  return result;
}

function getBundleGroupKey(item: CartItem): string | undefined {
  const bundleInstanceId = item.bundle_instance_id;
  if (typeof bundleInstanceId === 'string' && bundleInstanceId.length > 0) {
    return bundleInstanceId;
  }

  if (
    item.bundleId !== null &&
    item.bundleId !== undefined &&
    Number.isFinite(Number(item.bundleId))
  ) {
    return `bundle-id:${item.bundleId}`;
  }

  return undefined;
}

function bundleDisplayName(item: CartItem): string {
  const snapshotName = item.presentationSnapshot?.name;
  if (typeof snapshotName === 'string' && snapshotName.trim().length > 0) {
    return snapshotName;
  }

  const bundleName = item.bundle_name;
  if (typeof bundleName === 'string' && bundleName.trim().length > 0) {
    return bundleName;
  }

  return item.bundleId !== null && item.bundleId !== undefined
    ? 'Bundle'
    : (item.name ?? 'Bundle');
}

/**
 * Strips the trailing " for {participant name}" suffix that Clare's
 * CartService appends to cart-item names.  Only removes the suffix when the
 * item carries a participant reference whose name matches exactly, so
 * legitimate product names are never affected.
 */
function stripParticipantSuffix(item: CartItem): string {
  const name = item.name ?? '';
  const participantName = item.references?.participant?.name;
  if (participantName) {
    const suffix = ` for ${participantName}`;
    if (name.endsWith(suffix)) {
      return name.slice(0, -suffix.length);
    }
  }
  return name;
}

function buildBundleLine(item: CartItem): string {
  const name = stripParticipantSuffix(item);
  const specs = buildCartItemSpecLabel(item);
  if (name && specs) {
    return `${name} • ${specs}`;
  }
  return name || specs || 'Bundle item';
}
